import moment from 'moment';
import React, {useEffect, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Paper, IconButton, Dialog, DialogTitle, DialogActions, Button, DialogContent, Box, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import DetailsIcon from '@mui/icons-material/Details';
import { DataGrid } from '@mui/x-data-grid';

import LoadingBox from '../../utils/LoadingBox';
import { displayClientName } from '../../utils/Utils';
import ServiceChip from '../../utils/ServiceChip';
import { getBookings } from './BookingsSlice';
import { getOperation, unsetOperation } from  '../operations/OperationsSlice';

function Bookings () {
    const { isLoading, bookings } = useSelector((state) => state.bookings);
    const { op } = useSelector((state) => state.ops);
    const opIsLoading = useSelector((state) => state.ops.isLoading);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [dataFetched, setDataFetched] = useState(false);
    const [showOp, setShowOp] = useState(false);

    useEffect(() => {
        if(Object.keys(bookings).length === 0 && !dataFetched) {
            dispatch(getBookings());
            setDataFetched(true);
        }
    }, [bookings, dataFetched, dispatch]);

    const showOpDialog = (opId) => {
        dispatch(unsetOperation());
        dispatch(getOperation(opId));
        setShowOp(true);
    }

    const sortedBookings = sortBookings(bookings);

    const columns = [
        {field: 'status', headerName: 'Status', renderCell: (params) => (
            <div>
                {params.value === '3' && <IconButton aria-label='Completed'><CheckCircleOutlineIcon color='success' /></IconButton>}
                {params.value === '4' && <IconButton aria-label='Cancelled'><CancelIcon color='warning' /></IconButton>}
            </div>
        )},
        {field: 'service_type', headerName: 'Service', width: 110, renderCell: (params) => (
            <ServiceChip serviceType={params.value} />
        )},
        {field: 'reference', headerName: 'Reference'},
        {field: 'client', headerName: 'Client'},
        {field: 'vehicle', headerName: 'Car'},
        {field: 'from', headerName: 'From date'},
        {field: 'to', headerName: 'To date'},
        {field: 'value', headerName: 'Rental value'},
        {field: 'details', headerName: 'Details', type: 'actions', getActions: (params) => [
            <IconButton aria-label='Details' onClick={() => navigate(`/manage/bookings/${params.row.id}/details`)}>
                <DetailsIcon color='info' />
            </IconButton>
        ]},
        {field: 'ops', headerName: 'Ops', type: 'actions', getActions: (params) => [
            <IconButton aria-label='Ops' onClick={() => showOpDialog(params.row.ops)}>
                <DetailsIcon color='info' />
            </IconButton>
        ]},
        {field: 'gallery', headerName: 'Images', type: 'actions', getActions: (params) => [
            <IconButton aria-label='Images' onClick={() => navigate(`/manage/bookings/${params.row.reference}/gallery`)} >
                <ImageSearchIcon color='info' />
            </IconButton>
        ]}
    ];
    const rows = sortedBookings.map((booking) => (
        {
            id: booking.id,
            status: booking.status,
            service_type: booking.service_type || 'Rental',
            reference: booking.reference || '',
            client: booking.client ? displayClientName(booking.client) : '',
            vehicle: booking.vehicle 
                ? `${booking.vehicle.registration || ''} ${booking.vehicle.model?.vehicle_make || ''} ${booking.vehicle.model?.vehicle_model || ''}`.trim() 
                : '',
            from: booking.pickup_datetime ? moment(booking.pickup_datetime).format('D-MMM-YY') : '',
            to: booking.dropoff_datetime ? moment(booking.dropoff_datetime).format('D-MMM-YY') : '',
            value: booking.booking_value || '',
            ops: booking.booking_operation
        }
    ));

    return (
        <Container>
            <Typography variant='h3' align='center' gutterBottom>
                Bookings
            </Typography>
            <LoadingBox isLoading={isLoading} elements={bookings} noneText={'No bookings'} />
            <Paper sx={{padding: 2}}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id || Math.random().toString(36).substring(2, 15)}
                    autoHeight
                    initialState={{
                        pagination: {
                            paginationModel: {page: 0, pageSize: 10},
                        },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                />
            </Paper>
            <Dialog open={showOp} onClose={() => setShowOp(false)} aria-labelledby='op-dialog-title' aria-describedby='op-dialog-content'>
                <DialogTitle id='op-dialog-title'>Operations details</DialogTitle>
                <DialogContent>
                    <OpDetails isLoading={opIsLoading} op={op} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowOp(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default Bookings;


export const sortBookings = (bookings) => {
    if (!bookings || !Array.isArray(bookings) || bookings.length === 0) {
        return []
    }
    return bookings.slice().sort((a, b) => {
        const dateA = a?.pickup_datetime ? new Date(a.pickup_datetime).getTime() : 0;
        const dateB = b?.pickup_datetime ? new Date(b.pickup_datetime).getTime() : 0;
        return dateA - dateB;
    });
}

function OpDetails ({isLoading, op}) {
    if (!op || typeof op !== 'object') {
        return null;
    }
    return (<>
        {isLoading ? (
            <Box sx={{display: 'flex', justifyContent: 'center' }}> <CircularProgress size={24} /> </Box>
        ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2 }}>
                <List>
                    <ListItem key='dispatch_driver'>
                        <ListItemText
                            primary={'Dispatch driver'}
                            secondary={Array.isArray(op.dispatch_driver) ? op.dispatch_driver.map(driver => `${driver.first_name || ''} ${driver.last_name || ''}`.trim()).filter(Boolean).join(', ') : ''}
                        />
                    </ListItem>
                    <ListItem key='dispatch-details'>
                        <ListItemText 
                            primary={'Dispatch data'}
                            secondary={`Dispatch km: ${op.dispatch_km ?? ''}, dispatch fuel: ${op.dispatch_fuel ?? ''}`}
                        />
                    </ListItem>
                    <ListItem key='return_driver'>
                        <ListItemText
                            primary={'Return driver'}
                            secondary={Array.isArray(op.return_driver) ? op.return_driver.map(driver => `${driver.first_name || ''} ${driver.last_name || ''}`.trim()).filter(Boolean).join(', ') : ''}
                        />
                    </ListItem>
                    <ListItem key='return-details'>
                        <ListItemText 
                            primary={'Return data'}
                            secondary={`Return km: ${op.return_km ?? ''}, return fuel: ${op.return_fuel ?? ''}`}
                        />
                    </ListItem>
                    <ListItem key='remarks'>
                        <ListItemText 
                            primary={'Remarks'}
                            secondary={op.remarks || ''}
                        />
                    </ListItem>
                </List>
            </Box>
        )}
        </>
    )
}